// Code generated by balancegen from data/contracts/inboxledger.toml. DO NOT EDIT.
// Regenerate with scripts/gen-data.sh.
//
// The inbox ledger (inbox.md §5, R1831). Compile-time lane (R310): -balance does not move these values at
// boot. A change needs the regeneration and a rebuild of both sides.

/** The counters of a ledger row, in row order: index i of `InboxLedgerRow.counts` is key i. */
export const INBOX_LEDGER_KEYS = [
  /** loss, messages: fleet_lost, fleet_lost_cargo, fleet_lost_by, room_made and cargo_spilled, one per event. */
  "msg_loss",
  /** war, messages: fleet_killed, torpedo_hit that hit, torpedo_struck, battle_report, captain_taken, sea_mine_struck, sea_mine_tripped and sea_object_lost, one per event. */
  "msg_war",
  /** hold, messages: hold_under_fire, territory_lost, siege_defend, hold_moved, object_fallen, object_mauled and object_held, one per event. */
  "msg_hold",
  /** dive, messages: the four expedition returns, one per event. */
  "msg_dive",
  /** trade, messages: market_sold_isotopes, market_sold_print and club_paid, one per event. */
  "msg_trade",
  /** world, messages: season_closed, pickup_squad, achievement, redraft_done and featured_next, one per event. */
  "msg_world",
  /** loss, count: fleet_lost, fleet_lost_cargo and fleet_lost_by, one per fleet (a fleet is one hull). */
  "fleets_lost",
  /** loss, count: a loss whose fate is captured. */
  "captains_captured",
  /** loss, count: a loss whose fate is wounded, and expedition_hurt and expedition_hurt_core. */
  "captains_hurt",
  /** loss, amount: units: cargo of fleet_lost_cargo, and the spilled units of cargo_spilled. */
  "cargo_lost",
  /** loss, amount: the damage to own hulls that sank: the damage figure of fleet_lost, fleet_lost_cargo and fleet_lost_by, or the sum of their damage rows. */
  "dmg_taken",
  /** war, count: fleet_killed, one per enemy fleet sunk. */
  "kills",
  /** war, amount: the damage to enemy hulls that sank: the damage figure of fleet_killed, or the sum of its dealt rows. */
  "dmg_dealt",
  /** war, count: torpedo_hit whose count is above 0, one per detonation of an own torpedo that hit. */
  "torp_hit",
  /** war, count: torpedo_struck, one per detonation that struck own hulls. */
  "torp_struck",
  /** war, count: battle_report, one per window battle. */
  "battles",
  /** war, amount: units: prize_alloy, prize_biomass and prize_volatiles of fleet_killed. */
  "salvage",
  /** hold, count: hold_under_fire, one per fire episode (the later shots of an episode never reach the router). */
  "fire_events",
  /** hold, count: cells of territory_lost (one event per flip burst), 1 when the event names none. */
  "cells_lost",
  /** hold, count: the rise of the season counter of cells taken, read at each hour close. */
  "cells_won",
  /** hold, count: battle_report whose outcome is taken. */
  "obj_taken",
  /** hold, count: object_fallen of an own object, and battle_report whose outcome is lost. */
  "obj_lost",
  /** hold, count: object_held of an own object, and battle_report whose outcome is held. */
  "obj_held",
  /** dive, count: the four expedition returns, one per dive. */
  "dives",
  /** dive, amount: isotopes of an expedition return. */
  "isotopes",
  /** dive, count: expedition_core and expedition_hurt_core, one per Data Core. */
  "cores",
  /** trade, count: market_sold_isotopes and market_sold_print, one per sale. */
  "sales",
  /** trade, amount: price of a sale. */
  "credits",
  /** works, count: queue_done, one per building job. */
  "jobs_done",
  /** works, count: research_done, one per tech node. */
  "research_done",
  /** works, count: ship_ready, one per hull. */
  "ships_ready",
  /** works, count: repair_done, one per repair. */
  "repairs",
  /** works, count: torpedo_stowed, one per job. */
  "torps_stowed",
  /** works, count: captain_recruited, one per captain. */
  "captains_recruited",
  /** works, count: captain_practised, one per point. */
  "practice",
  /** works, amount: units of cargo_landed. */
  "cargo_landed",
  /** works, count: fleet_docked, one per docking. */
  "dockings",
  /** works, amount: qty of market_bought_isotopes. */
  "iso_bought",
  /** works, count: market_bought_print, one per print. */
  "prints_bought",
  /** works, count: cargo_blocked, one per landing that found the storage full. */
  "cargo_blocked",
] as const;

/** One counter of the inbox ledger. */
export type InboxLedgerKey = (typeof INBOX_LEDGER_KEYS)[number];

/** The bucket of a counter, or works for the events that file no row. */
export type InboxLedgerGroup = 'loss' | 'war' | 'hold' | 'dive' | 'trade' | 'world' | 'works';

/** What a counter counts: the messages of its bucket, things, or the sum of a figure. */
export type InboxLedgerKind = 'messages' | 'count' | 'amount';

/** The group and the kind of each counter. */
export const INBOX_LEDGER_ROWS: Readonly<Record<InboxLedgerKey, { group: InboxLedgerGroup; kind: InboxLedgerKind }>> = {
  msg_loss: { group: "loss", kind: "messages" },
  msg_war: { group: "war", kind: "messages" },
  msg_hold: { group: "hold", kind: "messages" },
  msg_dive: { group: "dive", kind: "messages" },
  msg_trade: { group: "trade", kind: "messages" },
  msg_world: { group: "world", kind: "messages" },
  fleets_lost: { group: "loss", kind: "count" },
  captains_captured: { group: "loss", kind: "count" },
  captains_hurt: { group: "loss", kind: "count" },
  cargo_lost: { group: "loss", kind: "amount" },
  dmg_taken: { group: "loss", kind: "amount" },
  kills: { group: "war", kind: "count" },
  dmg_dealt: { group: "war", kind: "amount" },
  torp_hit: { group: "war", kind: "count" },
  torp_struck: { group: "war", kind: "count" },
  battles: { group: "war", kind: "count" },
  salvage: { group: "war", kind: "amount" },
  fire_events: { group: "hold", kind: "count" },
  cells_lost: { group: "hold", kind: "count" },
  cells_won: { group: "hold", kind: "count" },
  obj_taken: { group: "hold", kind: "count" },
  obj_lost: { group: "hold", kind: "count" },
  obj_held: { group: "hold", kind: "count" },
  dives: { group: "dive", kind: "count" },
  isotopes: { group: "dive", kind: "amount" },
  cores: { group: "dive", kind: "count" },
  sales: { group: "trade", kind: "count" },
  credits: { group: "trade", kind: "amount" },
  jobs_done: { group: "works", kind: "count" },
  research_done: { group: "works", kind: "count" },
  ships_ready: { group: "works", kind: "count" },
  repairs: { group: "works", kind: "count" },
  torps_stowed: { group: "works", kind: "count" },
  captains_recruited: { group: "works", kind: "count" },
  practice: { group: "works", kind: "count" },
  cargo_landed: { group: "works", kind: "amount" },
  dockings: { group: "works", kind: "count" },
  iso_bought: { group: "works", kind: "amount" },
  prints_bought: { group: "works", kind: "count" },
  cargo_blocked: { group: "works", kind: "count" },
};

/** The length of the hour ring: the wall hours of the last two days. */
export const INBOX_LEDGER_HOUR_ROWS = 48;

/** The bound on the day rows of one season. */
export const INBOX_LEDGER_DAY_ROWS = 112;
